
import { supabase } from '@/lib/supabase';
import { adminLog, adminError } from '@/lib/utils/admin-logger';
import { ensureBotUserExists } from '@/lib/api/bot-user';
import { isGdprObjectionSkip, showAIObjectionToast } from '@/lib/ai/objectionNotice';
import { readFunctionErrorKey, showInsufficientCreditsToast } from '@/lib/insufficientCredits';

export const useAIReview = () => {
  /**
   * Trigger an AI review for a paper.
   *
   * IMPORTANT: `version` is required. Callers MUST pass the explicit
   * `paper_version_at_review` they want the review tied to (e.g. the
   * paper's `currentVersion` or the version of an existing review being
   * regenerated). Passing the wrong version causes duplicate reviews
   * because the edge function dedups per `paper_version_at_review`.
   */
  const triggerAIReview = async (
    pdfUrl: string,
    paperId: string,
    title: string,
    abstract: string,
    forceRegenerate: boolean,
    version: string
  ) => {
    try {
      adminLog(`Starting AI review generation for paper ${paperId}`);
      
      // Get the bot user ID
      const botUserId = await ensureBotUserExists();
      if (!botUserId) {
        throw new Error('Could not get bot user ID for AI review generation');
      }
      
      // Get external URL and visibility from the database
      const { data: paperData, error: fetchError } = await supabase
        .from('papers')
        .select('external_url, visibility, current_version, category')
        .eq('id', paperId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch paper data: ${fetchError.message}`);
      }

      const response = await supabase.functions.invoke('generate-ai-review', {
        body: {
          paperId,
          pdfUrl,
          title,
          abstract,
          reviewerId: botUserId,
          externalUrl: paperData?.external_url,
          forceRegenerate,
          isPrivatePaperReview: paperData?.visibility === 'private',
          paperVersion: version || paperData?.current_version || '1.0',
          paperCategory: paperData?.category
        }
      });

      if (response.error) {
        const errorKey = await readFunctionErrorKey(response.error);
        if (errorKey === 'insufficient_credits') {
          showInsufficientCreditsToast('review');
          return null;
        }
        adminError('AI review generation failed:', response.error);
        throw new Error(response.error.message);
      }

      if (isGdprObjectionSkip(response.data)) {
        adminLog('AI review skipped — GDPR objection on file');
        showAIObjectionToast();
        return response.data;
      }

      adminLog('AI review generation initiated successfully');
      return response.data;
    } catch (error) {
      adminError('Error in triggerAIReview:', error);
      throw error;
    }
  };

  const triggerKeywordExtraction = async (
    pdfUrl: string,
    paperId: string,
    title: string,
    abstract: string,
    currentKeywords: string[] = []
  ) => {
    try {
      adminLog(`Starting keyword and research info extraction for paper ${paperId}`);
      
      // Get external URL from the database
      const { data: paperData, error: fetchError } = await supabase
        .from('papers')
        .select('external_url')
        .eq('id', paperId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch paper data: ${fetchError.message}`);
      }

      const response = await supabase.functions.invoke('extract-paper-data', {
        body: {
          paperId,
          pdfUrl,
          title,
          abstract,
          currentKeywords,
          externalUrl: paperData?.external_url
        }
      });

      if (response.error) {
        adminError('Keyword and research info extraction failed:', response.error);
        throw new Error(response.error.message);
      }

      if (isGdprObjectionSkip(response.data)) {
        adminLog('Metadata extraction skipped — GDPR objection on file');
        showAIObjectionToast();
        return response.data;
      }

      adminLog('Keyword and research info extraction initiated successfully');
      return response.data;
    } catch (error) {
      adminError('Error in triggerKeywordExtraction:', error);
      throw error;
    }
  };

  return {
    triggerAIReview,
    triggerKeywordExtraction
  };
};
